import React from 'react'

import type {
    SignatureContent,
    SignatureCustomLine,
} from '../../../common/models/signature-tool/signature-tool.js'

export type CustomLineType = SignatureCustomLine

export interface SignatureProps extends SignatureContent {
    signatureRef?: React.Ref<HTMLTableElement>
}

const LINK_COLORS = {
    play: '#22A085',
    nope: '#697EF7',
    default: '#1C826C',
} as const

const getLinkColor = (isPlay: boolean, isNope: boolean): string => {
    const color = [isPlay && LINK_COLORS.play, isNope && LINK_COLORS.nope].find(Boolean)

    return (color as string | undefined) ?? LINK_COLORS.default
}

const renderContactLink = (label: string, value: string, href: string, linkColor: string) => (
    <p style={styles.custom}>
        {label}:{' '}
        <a href={href} style={{ ...styles.link, color: linkColor }}>
            {value}
        </a>
    </p>
)

const renderCustomLine = (
    line: CustomLineType,
    index: number,
    linkColor: string,
): React.ReactNode => {
    if (!line.key || line.key.trim() === '') {
        return null
    }

    return (
        <p key={index} style={styles.custom}>
            {line.key}
            {line.value && ': '}
            {line.link ? (
                <a href={line.link} style={{ ...styles.link, color: linkColor }}>
                    {line.value || line.key}
                </a>
            ) : (
                line.value
            )}
        </p>
    )
}

interface LogoConfig {
    url: string
    imageUrl: string
    width: string
    height: string
    alt: string
}

const renderLogo = (config: LogoConfig) => (
    <p style={styles.logoBlock}>
        <a href={config.url} style={styles.noUnderline}>
            <img
                src={
                    config.imageUrl.startsWith('/')
                        ? `${window.location.origin}${config.imageUrl}`
                        : config.imageUrl
                }
                width={config.width}
                height={config.height}
                alt={config.alt}
            />
        </a>
    </p>
)

// The NOPE and alliance files are the 2026 rebrand assets, Hub-hosted (shipped in
// src/server/assets/signature/, served at /cvi/signature/*) — root-relative paths
// get the app origin prefixed at render time so the copied signature carries an
// absolute URL that mail clients can fetch. Both are transparent PNGs with a white
// sticker shape baked in behind the lettering (near-invisible on light emails, a
// sticker on dark ones). The gotoAndPlay logo has no new file and stays on the
// play.ee CVI server.
const LOGO_CONFIGS = {
    play: {
        url: 'https://play.ee',
        imageUrl: 'https://play.ee/cvi/signature/img/gotoandplay_logo_dark.png',
        width: '97',
        height: '20',
        alt: 'gotoAndPlay OÜ',
    },
    nope: {
        url: 'https://nope.ee',
        imageUrl: '/cvi/signature/nope-logo-2x.png',
        width: '57',
        height: '18',
        alt: 'Nope OÜ',
    },
    alliance: {
        url: 'https://playandnope.com',
        imageUrl: '/cvi/signature/alliance-2x.png',
        width: '74',
        height: '46',
        alt: 'part of play & nope alliance',
    },
} as const

export const Signature: React.FC<SignatureProps> = ({
    name,
    title,
    email,
    website,
    imageUrl,
    customLines = [],
    playLogo,
    nopeLogo,
    allianceLogo,
    isPlay,
    isNope,
    signatureRef,
    isDark,
}) => {
    const linkColor = getLinkColor(isPlay, isNope)
    const textColor = isDark ? '#ffffff' : '#282828'

    return (
        <table
            ref={signatureRef}
            style={{
                fontFamily: 'Verdana, sans-serif',
                fontSize: '12px',
                margin: '0 0 0 24px',
                color: textColor,
            }}
        >
            <tbody>
                <tr>
                    {imageUrl && (
                        <td
                            style={{
                                width: '90px',
                                height: '90px',
                                verticalAlign: 'middle',
                                padding: '0 19px 0 0',
                            }}
                        >
                            <img src={imageUrl} alt={name} width="90" height="90" />
                        </td>
                    )}

                    <td
                        style={{
                            height: '60px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            lineHeight: '20px',
                            padding: 0,
                            verticalAlign: 'middle',
                        }}
                    >
                        <strong>{name}</strong>
                        <br />
                        <span style={{ fontSize: '14px', fontWeight: 'normal' }}>{title}</span>
                    </td>
                </tr>

                <tr>
                    {imageUrl && <td></td>}

                    <td>
                        {email && renderContactLink('Email', email, `mailto:${email}`, linkColor)}
                        {website &&
                            renderContactLink('Web', website, `https://${website}`, linkColor)}
                        {customLines.map((line, i) => renderCustomLine(line, i, linkColor))}
                        {playLogo && renderLogo(LOGO_CONFIGS.play)}
                        {nopeLogo && renderLogo(LOGO_CONFIGS.nope)}
                        {allianceLogo && renderLogo(LOGO_CONFIGS.alliance)}
                    </td>
                </tr>
            </tbody>
        </table>
    )
}

const styles = {
    custom: {
        fontFamily: 'Verdana, sans-serif',
        fontSize: '12px',
        lineHeight: '12px',
        margin: '8px 0',
        padding: 0,
    },
    link: {
        textDecoration: 'none',
    },
    logoBlock: {
        margin: '16px 0 0 0',
        lineHeight: '12px',
    },
    noUnderline: {
        textDecoration: 'none',
    },
} as const
